import React from 'react';
import { Button, Spinner, Tooltip, makeStyles, shorthands } from '@fluentui/react-components';
import { ArrowUndo16Regular } from '@fluentui/react-icons';
import { ReactComponent as PromptEnhancerIcon } from '../../../../../assets/toolkit/prompt-enhancer.svg';
import { customColors, customFonts } from '../../../../../styles';

const trayButtonIcon = '& .toolkitTrayIcon';
const trayButtonIconSvg = '& .toolkitTrayIcon svg';

const useStyles = makeStyles({
    trayButton: {
        height: '32px',
        fontFamily: customFonts.Lato,
        fontSize: '14px',
        fontWeight: '700',
        color: customColors.protivitiDarkBlue,
        ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),
        ...shorthands.borderRadius('5px'),
        ...shorthands.padding('0px', '12px'),
        backgroundColor: customColors.white,
        whiteSpace: 'nowrap',
        transition: 'all 0.2s ease',

        [trayButtonIconSvg]: {
            color: customColors.protivitiDarkBlue,
            transition: 'all 0.2s ease',
        },

        '&:hover:enabled': {
            backgroundColor: customColors.protivitiDarkBlue,
            color: customColors.white,
            ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),

            [trayButtonIcon]: {
                backgroundColor: 'transparent',
            },

            [trayButtonIconSvg]: {
                color: customColors.white,
            },
        },

        '&:active:enabled': {
            backgroundColor: customColors.protivitiDarkBlue,
            color: customColors.white,
            transition: 'all 0s',

            [trayButtonIcon]: {
                backgroundColor: 'transparent',
            },

            [trayButtonIconSvg]: {
                color: customColors.white,
            },
        },
        '&:disabled, &:disabled:hover, &:disabled:active, &:disabled:focus, &:disabled:focus-within': {
            backgroundColor: customColors.white,
            color: 'lightgray',
            ...shorthands.border('1px', 'solid', 'lightgray'),
            cursor: 'not-allowed',

            [trayButtonIconSvg]: {
                color: 'lightgray',
            },
        },
    },
    tooltipWrapper: {
        display: 'inline-flex',
    },
});

interface PromptEnhancerButtonProps {
    canEnhancePrompt: boolean;
    canUndoPromptEnhancement: boolean;
    isPromptEnhancing: boolean;
    onEnhancePrompt: () => void;
    onUndoPromptEnhancement: () => void;
}

const PromptEnhancerButton: React.FC<PromptEnhancerButtonProps> = ({
    canEnhancePrompt,
    canUndoPromptEnhancement,
    isPromptEnhancing,
    onEnhancePrompt,
    onUndoPromptEnhancement,
}) => {
    const styles = useStyles();
    const isDisabledBecausePromptIsEmpty = !isPromptEnhancing && !canEnhancePrompt && !canUndoPromptEnhancement;
    const isDisabled = isPromptEnhancing || isDisabledBecausePromptIsEmpty;
    const shouldShowTooltip = !isPromptEnhancing;
    const tooltipContent = isDisabledBecausePromptIsEmpty
        ? 'Enter a prompt to enhance it first.'
        : canUndoPromptEnhancement
          ? 'Undo the last prompt enhancement.'
          : 'Prompt Enhancer will refine your prompt to help produce a stronger response.';

    const handleClick = (): void => {
        if (canUndoPromptEnhancement) {
            onUndoPromptEnhancement();
            return;
        }

        onEnhancePrompt();
    };

    return (
        <Tooltip content={tooltipContent} relationship="description" visible={shouldShowTooltip ? undefined : false}>
            <span className={styles.tooltipWrapper}>
                <Button
                    className={styles.trayButton}
                    appearance="transparent"
                    onClick={handleClick}
                    disabled={isDisabled}
                    icon={
                        isPromptEnhancing ? (
                            <Spinner size="tiny" />
                        ) : canUndoPromptEnhancement ? (
                            <ArrowUndo16Regular />
                        ) : (
                            <PromptEnhancerIcon className="toolkitTrayIcon" />
                        )
                    }
                >
                    {isPromptEnhancing ? 'Enhancing...' : 'Prompt Enhancer'}
                </Button>
            </span>
        </Tooltip>
    );
};

export default PromptEnhancerButton;
